import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

/** Seletor pesquisável de funcionário: escolha única, apenas nomes vindos da base (sem digitação livre). */
export const StaffPicker = ({ label, options, value, onChange, testId, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClickAway = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [open]);

  const selected = (options || []).find((o) => o.id === value) || null;
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return (options || []).filter((o) => o.name.toLowerCase().includes(term));
  }, [options, query]);

  const pick = (id) => {
    onChange(id);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={boxRef} className="relative">
      <label className="block text-[13px] font-semibold uppercase tracking-[0.08em] text-ink-600">{label}</label>
      <button
        type="button"
        data-testid={testId}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="mt-2 flex h-11 w-full items-center justify-between gap-2 rounded-xl border border-border bg-white px-3.5 text-left text-[14.5px] text-ink-800 transition-colors hover:border-sage-600/50 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15 disabled:opacity-60"
      >
        <span className={selected ? "" : "text-ink-400"}>{selected ? selected.name : "Não definido"}</span>
        <span className="flex items-center gap-1.5">
          {selected ? (
            <span
              role="button"
              tabIndex={0}
              aria-label={`Limpar ${label}`}
              data-testid={testId ? `${testId}-clear` : undefined}
              onClick={(e) => {
                e.stopPropagation();
                pick(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  pick(null);
                }
              }}
              className="grid h-6 w-6 place-items-center rounded-full text-ink-400 hover:bg-sand hover:text-ink-700"
            >
              <X size={13} aria-hidden="true" />
            </span>
          ) : null}
          <ChevronDown size={16} className="text-ink-400" aria-hidden="true" />
        </span>
      </button>

      {open ? (
        <div
          data-testid={testId ? `${testId}-popover` : undefined}
          className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-border bg-white shadow-[0_12px_28px_rgba(16,24,40,0.12)]"
        >
          <label className="relative flex items-center border-b border-border">
            <Search size={15} className="absolute left-3.5 text-ink-400" aria-hidden="true" />
            <input
              autoFocus
              data-testid={testId ? `${testId}-search` : undefined}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Buscar ${label.toLowerCase()}`}
              className="h-11 w-full pl-10 pr-3 text-[14.5px] text-ink-800 placeholder:text-ink-400 focus:outline-none"
            />
          </label>
          <ul className="max-h-56 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                data-testid={testId ? `${testId}-option-none` : undefined}
                onClick={() => pick(null)}
                className="flex w-full items-center justify-between px-3.5 py-2.5 text-left text-[14.5px] text-ink-600 hover:bg-sand"
              >
                Não definido
                {!selected ? <Check size={15} className="text-sage-700" aria-hidden="true" /> : null}
              </button>
            </li>
            {filtered.length === 0 ? (
              <li
                data-testid={testId ? `${testId}-empty` : undefined}
                className="px-3.5 py-3 text-[13.5px] text-ink-500"
              >
                Nenhum funcionário ativo com este cargo.
              </li>
            ) : (
              filtered.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    data-testid={testId ? `${testId}-option-${o.id}` : undefined}
                    onClick={() => pick(o.id)}
                    className="flex w-full items-center justify-between px-3.5 py-2.5 text-left text-[14.5px] text-ink-800 hover:bg-sand"
                  >
                    {o.name}
                    {o.id === value ? <Check size={15} className="text-sage-700" aria-hidden="true" /> : null}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
};

export const CARE_TEAM_FIELDS = [
  { key: "counselor", field: "counselor_id", label: "Conselheiro" },
  { key: "psychologist", field: "psychologist_id", label: "Psicólogo(a)" },
  { key: "psychiatrist", field: "psychiatrist_id", label: "Psiquiatra" },
];

/** Três seletores independentes, um por cargo da equipe responsável. */
export const CareTeamFields = ({ options, values, onChange, testPrefix = "care-team", disabled = false, stacked = false }) => (
  <div className={`grid gap-5 ${stacked ? "" : "sm:grid-cols-3"}`}>
    {CARE_TEAM_FIELDS.map(({ key, field, label }) => (
      <StaffPicker
        key={key}
        label={label}
        options={options?.[key]?.items || []}
        value={values[field] || null}
        onChange={(id) => onChange({ ...values, [field]: id })}
        testId={`${testPrefix}-${key}`}
        disabled={disabled}
      />
    ))}
  </div>
);
